import { configureStore, createAction } from '@reduxjs/toolkit'
import type { ThunkDispatch, AnyAction } from '@reduxjs/toolkit'
import type { Selector } from 'react-redux'
import { combineReducers } from 'redux'
import { FLUSH, PAUSE, PERSIST, PersistConfig, persistReducer, persistStore, PURGE, REGISTER, REHYDRATE } from 'redux-persist'
import { PersistPartial } from 'redux-persist/lib/persistReducer'
import storageSession from 'redux-persist/lib/storage/session'
import { apiService } from '@afterpaytouch/portal-api/api'
import { apiService as topazService } from '@afterpaytouch/topaz-api/api'
import { apiService as orderStatusService } from '@afterpaytouch/order-status-api/api'
import { modalReducer, modalInitialState } from '@afterpaytouch/core'
import { appleGatewayService } from './appleGateway/apis'

import type { AuthenticationState, PasswordResetState } from '.'
import {
  applePayInitialState,
  oAuthInitialState,
  applePayReducer,
  applePaylaterReducer,
  applePaylaterInitialState,
  checkoutInitialState,
  checkoutReducer,
  consumerInitialState,
  consumerReducer,
  countryInitialState,
  countryReducer,
  cardScanInitialState,
  cardScanReducer,
  createAuthSessionsApi,
  createPaymentSourceCaptureApi,
  createPortalApi,
  createTopazApi,
  experimentInitialState,
  experimentsReducer,
  passwordInitialState,
  passwordReducer,
  registrationInitialState,
  registrationReducer,
  shippingAddressInitialState,
  shippingAddressReducer,
  shippingOptionInitialState,
  shippingOptionReducer,
  topazInitialState,
  tagsReducer,
  topazReducer,
  userInitialState,
  userReducer,
  oAuthReducer,
  donationReducer,
  donationInitialState,
  consumerLendingReducer,
  recoverableTerminalErrorsReducer,
  biometricReducer,
  biometricInitialState,
  recoverableErrorsInitialState,
  tagsInitialState,
  consumerLendingInitialState,
  processingReducer,
  giftcardEligibilityReducer,
  convergenceInitialState,
  convergenceReducer,
  preferredPaymentDayReducer,
  preferredPaymentDayInitialState,
  ppaModalReducer,
  ppaInitialState,
  uiControlReducer,
  uiControlInitialState,
  featureFlagsReducer,
  featureFlagsInitialState,
  identityReducer,
  externalLinkIframeReducer,
  externalLinkIframeInitialState,
  authSessionsFlowReducer,
  authSessionsFlowInitialState,
} from '.'
import { identityInitialState } from './identity/reducer'
import cashAuthReducer, { cashAuthInitialState } from './cashAuth/reducer'
import userJourneyReducer, { userJourneyInitialState } from './userJourney/reducer'
import { DEBUG_MODE } from '../env'
import { memoize } from '../utils/memoize'
import { cleanupRTKQueryState } from './utils'
import { ThunkExtraArgs } from './types'

interface CombinedAuthState {
  resetPassword: PasswordResetState
  user: AuthenticationState
}

const { reducer: apiReducer, middleware: apiMiddleware } = apiService.service

const { reducer: topazApiReducer, middleware: topazMiddleware } = topazService.service

const { reducer: orderStatusApiReducer, middleware: orderStatusMiddleware } = orderStatusService.service

const { reducer: appleGatewayApiReducer, middleware: appleGatewayApiMiddleWare } = appleGatewayService.service

export const authInitialState: CombinedAuthState = {
  resetPassword: passwordInitialState,
  user: userInitialState,
}

export const initialState = {
  auth: authInitialState,
  applepay: applePayInitialState,
  applePaylater: applePaylaterInitialState,
  consumer: consumerInitialState,
  checkout: checkoutInitialState,
  topaz: topazInitialState,
  country: countryInitialState,
  shippingAddress: shippingAddressInitialState,
  registration: registrationInitialState,
  shippingOption: shippingOptionInitialState,
  experiments: experimentInitialState,
  tags: tagsInitialState,
  consumerLending: consumerLendingInitialState,
  oAuth: oAuthInitialState,
  cardScan: cardScanInitialState,
  donation: donationInitialState,
  recoverableTerminalErrors: recoverableErrorsInitialState,
  biometric: biometricInitialState,
  convergence: convergenceInitialState,
  preferredPaymentDay: preferredPaymentDayInitialState,
  ppaModal: ppaInitialState,
  uiControl: uiControlInitialState,
  featureFlags: featureFlagsInitialState,
  modal: modalInitialState,
  identity: identityInitialState,
  externalLinkIframe: externalLinkIframeInitialState,
  cashAuth: cashAuthInitialState,
  authSessionsFlow: authSessionsFlowInitialState,
  userJourney: userJourneyInitialState,
}

export const resetStore = createAction('RESET_STATE')

export const combinedReducer = combineReducers({
  auth: combineReducers({
    resetPassword: passwordReducer,
    user: userReducer,
  }),
  applepay: applePayReducer,
  applePaylater: applePaylaterReducer,
  consumer: consumerReducer,
  checkout: checkoutReducer,
  topaz: topazReducer,
  country: countryReducer,
  shippingAddress: shippingAddressReducer,
  registration: registrationReducer,
  shippingOption: shippingOptionReducer,
  experiments: experimentsReducer,
  tags: tagsReducer,
  consumerLending: consumerLendingReducer,
  [apiService.service.reducerPath]: apiReducer,
  [topazService.service.reducerPath]: topazApiReducer,
  [orderStatusService.service.reducerPath]: orderStatusApiReducer,
  [appleGatewayService.service.reducerPath]: appleGatewayApiReducer,
  oAuth: oAuthReducer,
  cardScan: cardScanReducer,
  donation: donationReducer,
  recoverableTerminalErrors: recoverableTerminalErrorsReducer,
  biometric: biometricReducer,
  processing: processingReducer,
  giftcardEligibility: giftcardEligibilityReducer,
  convergence: convergenceReducer,
  preferredPaymentDay: preferredPaymentDayReducer,
  ppaModal: ppaModalReducer,
  uiControl: uiControlReducer,
  featureFlags: featureFlagsReducer,
  modal: modalReducer,
  identity: identityReducer,
  externalLinkIframe: externalLinkIframeReducer,
  cashAuth: cashAuthReducer,
  authSessionsFlow: authSessionsFlowReducer,
  userJourney: userJourneyReducer,
})

const ssr = typeof window === 'undefined'

const persistConfig: PersistConfig<unknown> = {
  key: 'apxo',
  version: 0.1,
  storage: storageSession,
  timeout: 2000,
  blacklist: ['cardScan', 'userJourney', 'authSessionsFlow'],
  transforms: [cleanupRTKQueryState],
}

const apis = {
  portalApi: memoize(() => createPortalApi()),
  topaz: memoize(() => createTopazApi()),
  paymentsSourceCapture: memoize(() => createPaymentSourceCaptureApi()),
  authSessionsApi: memoize(() => createAuthSessionsApi()),
}
const reducer = ssr ? combinedReducer : (persistReducer(persistConfig, combinedReducer) as unknown as typeof combinedReducer)

export const makeStore = () =>
  configureStore({
    reducer,
    preloadedState: initialState,
    middleware: (getDefaultMiddleware) =>
      getDefaultMiddleware({
        serializableCheck: {
          ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
        },
        thunk: { extraArgument: apis },
      }).concat(apiMiddleware, topazMiddleware, orderStatusMiddleware, appleGatewayApiMiddleWare),
    devTools: DEBUG_MODE,
  })

export const store = makeStore()

export const persistor = ssr ? null : persistStore(store)

export type CheckoutAppStore = typeof store
export type CheckoutAppState = ReturnType<typeof combinedReducer> & Partial<PersistPartial>
export type CheckoutAppDispatch = CheckoutAppStore['dispatch'] & ThunkDispatch<CheckoutAppState, ThunkExtraArgs, AnyAction>
export type CheckoutAppSelector<T> = Selector<CheckoutAppState, T>
